import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Space, Card, Grid, Skeleton, Tooltip, Popover } from "antd";
import { EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getTransactions } from '../TransactionsServices';
import { getUsers } from '../../Users/UsersServices';
import { debounce } from 'lodash';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useTranslation } from 'react-i18next';
import './TransactionTable.css';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { transactionFilterFields } from '../../Shared/Filters/filterConfig';
import AppTable from '../../Shared/AppTable/AppTable';
import { TABLE_CLASS, getRowClassName, getColumns } from './constants';
import PageToolbar from '../../Shared/PageToolbar';
import ExportButton from '../../DataExportImport/ExportButton';
import ImportButton from '../../DataExportImport/ImportButton';
import { Resource } from '../../../Constants/socket';

const { useBreakpoint } = Grid;

const TransactionTable = ({ onAction, onShowAddTransactionModal, refreshTable, onTransactionsLoaded, externalTransactions, softRefreshKey, getHighlightClass }) => {
    const { user, setLoading } = useGlobal();
    const { t } = useTranslation();

    const screens = useBreakpoint();
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [userOptionsLoading, setUserOptionsLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const [searchString, setSearchString] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort_by, setSortBy] = useState(undefined);
    const [sort_direction, setSortDirection] = useState(undefined);
    const [chipFilters, setChipFilters] = useState([]);
    const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        last: false,
    });
    const fetchTransactionsAbortControllerRef = useRef(null);
    const fetchUsersAbortControllerRef = useRef(null);
    const activeVisibleTransactionRequestRef = useRef(null);
    const latestFetchTransactionsRef = useRef(null);
    const latestSearchStringRef = useRef(searchString);
    const previousSoftRefreshKeyRef = useRef(softRefreshKey);

    const fetchUsers = useCallback(async (search = '') => {
        if (fetchUsersAbortControllerRef.current) {
            fetchUsersAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        fetchUsersAbortControllerRef.current = controller;

        try {
            setUserOptionsLoading(true);
            // Fetch users for the filter dropdown
            const response = await getUsers({ page: 1, size: 100, search_string: search, signal: controller.signal }); 
            const options = response.data.map(u => ({
                label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.name || u.email || u.id,
                value: u.id
            }));
            setUserOptions(options);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (fetchUsersAbortControllerRef.current === controller) {
                setUserOptionsLoading(false);
            }
        }
    }, [handleError]);

    const debouncedFetchUsers = React.useMemo(
        () => debounce(fetchUsers, 800),
        [fetchUsers]
    );

    useEffect(() => {
        if (user?.is_admin) {
            fetchUsers();
        }
    }, [user?.is_admin, fetchUsers]);

    // Use external transactions if provided (including empty arrays)
    useEffect(() => {
        if (externalTransactions) {
            setData(externalTransactions);
        }
    }, [externalTransactions]);

    const fetchTransactions = useCallback(async (search_string, options = {}) => {
        if (fetchTransactionsAbortControllerRef.current) {
            fetchTransactionsAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        fetchTransactionsAbortControllerRef.current = controller;

        try {
            if (!options?.silent) {
                activeVisibleTransactionRequestRef.current = controller;
                setTableLoading(true);
                setLoading(true);
            }
            const response = await getTransactions(
                { page, size: pageSize, sort_by, sort_direction, filters: normalizedChipFilters, search_string, signal: controller.signal },
                user.is_admin
            );
            setData(response.data);
            // Call the onTransactionsLoaded callback if provided
            if (onTransactionsLoaded) {
                onTransactionsLoaded(response.data);
            }
            setPagination({
                total: response.metadata.total,
                last: response.metadata.last,
            });
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (!options?.silent && activeVisibleTransactionRequestRef.current === controller) {
                activeVisibleTransactionRequestRef.current = null;
                setTableLoading(false);
                setLoading(false);
            }
        }
    }, [setLoading, handleError, page, pageSize, sort_by, sort_direction, normalizedChipFilters, user.is_admin, onTransactionsLoaded]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions, refreshTable]);

    useEffect(() => {
        latestFetchTransactionsRef.current = fetchTransactions;
        latestSearchStringRef.current = searchString;
    }, [fetchTransactions, searchString]);

    // Soft refresh used for socket-driven updates (avoids skeleton / global loading flip)
    useEffect(() => {
        if (softRefreshKey === undefined || softRefreshKey === null) {
            previousSoftRefreshKeyRef.current = softRefreshKey;
            return;
        }
        if (previousSoftRefreshKeyRef.current === softRefreshKey) return;

        previousSoftRefreshKeyRef.current = softRefreshKey;
        latestFetchTransactionsRef.current?.(latestSearchStringRef.current, { silent: true });
    }, [softRefreshKey]);

    const handleTableChange = (pagination, _filters, sorter) => {
        const { order, field } = sorter || {};
        const sort_by = field;
        const sort_direction = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined;
        if (pagination.current) setPage(pagination.current);

        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }
        if (sort_by) {
            setSortBy(sort_by);
        }
        if (sort_direction) {
            setSortDirection(sort_direction);
        }
    };

    const debouncedFetchTransactions = useCallback(
        debounce((search) => {
            fetchTransactions(search);
        }, 500),
        [fetchTransactions]
    );

    useEffect(() => {
        return () => {
            if (fetchTransactionsAbortControllerRef.current) {
                fetchTransactionsAbortControllerRef.current.abort();
            }
            if (fetchUsersAbortControllerRef.current) {
                fetchUsersAbortControllerRef.current.abort();
            }
            debouncedFetchUsers.cancel?.();
            debouncedFetchTransactions.cancel?.();
        };
    }, [debouncedFetchUsers, debouncedFetchTransactions]);

    const handleSearch = (e) => {
        setSearchString(e.target.value);
        debouncedFetchTransactions(e.target.value);
    };

    const handleChipFiltersChange = useCallback((nextFilters, normalized) => {
        setChipFilters(nextFilters);
        setNormalizedChipFilters(normalized);
        setPage(1);
    }, []);

    const handleChipSortChange = useCallback(({ sort_by: nextSortBy, sort_direction: nextSortDirection }) => {
        setSortBy(nextSortBy);
        setSortDirection(nextSortDirection);
        setPage(1);
    }, []);

    const filterFields = React.useMemo(() => {
        return transactionFilterFields.filter(field => {
            if ((field.key === 'user_id' || field.adminOnly) && !user?.is_admin) {
                return false;
            }
            return true;
        }).map(field => {
            if (field.key === 'user_id' || field.key === 'disabled_by_id') {
                return { 
                    ...field, 
                    type: 'enum',
                    options: userOptions,
                    loading: userOptionsLoading,
                    showSearch: true,
                    onSearch: debouncedFetchUsers,
                    filterOption: false 
                };
            }
            return field;
        });
    }, [userOptions, userOptionsLoading, user?.is_admin, debouncedFetchUsers]);

    const columns = React.useMemo(() => getColumns(t, user, screens, onAction), [t, user, screens, onAction]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <PageToolbar
                title={t('transactions')}
                actions={
                    <Space size={4}>
                        {user?.is_admin && (
                            <Button
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={onShowAddTransactionModal}
                            >
                                {!screens.xs && t('add_new_transaction')}
                            </Button>
                        )}
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            open={isMoreActionsOpen}
                            onOpenChange={setIsMoreActionsOpen}
                            content={
                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                    <ImportButton resource={Resource.TRANSACTION} type="text" size="small" />
                                    <ExportButton resource={Resource.TRANSACTION} filters={normalizedChipFilters} type="text" size="small" />
                                </Space>
                            }
                        >
                            <Tooltip title={t('more_actions')}>
                                <Button type="text" icon={<EllipsisOutlined />} size="medium" />
                            </Tooltip>
                        </Popover>
                    </Space>
                }
                search={{
                    placeholder: t('search'),
                    value: searchString,
                    onChange: handleSearch,
                    desktopWidth: 300,
                }}
                filters={
                    <SharedFilterBar
                        fields={filterFields}
                        value={chipFilters}
                        onChange={handleChipFiltersChange}
                        sortFields={[
                            { value: 'id', labelKey: 'id' },
                            { value: 'user_name', labelKey: 'user' },
                            { value: 'transaction_type', labelKey: 'transaction_type' },
                            { value: 'transaction_status', labelKey: 'transaction_status' },
                            { value: 'coins_amount', labelKey: 'coins' },
                            { value: 'is_disabled', labelKey: 'filters_field_disabled' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                            { value: 'disabled_at', labelKey: 'filters_field_disabled_at' },
                        ]}
                        sortValue={{ sort_by, sort_direction }}
                        onSortChange={handleChipSortChange}
                    />
                }
            />
            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                {tableLoading ? (
                    <AppTable
                        columns={columns.map(column => {
                            // Customize skeleton based on column type
                            switch (column.key) {
                                case 'id':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '60px' }}
                                            />
                                        )
                                    };
                                    
                                case 'user':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '120px' }}
                                            />
                                        )
                                    };

                                case 'transaction_type':
                                case 'transaction_status':
                                    return {
                                        ...column,
                                        render: () => (
                                            <div style={{ display: 'inline-flex', minWidth: 100 }}>
                                                <Skeleton.Button
                                                    active
                                                    size="small"
                                                    shape="round"
                                                    style={{
                                                        width: '100%',
                                                        height: 24,
                                                        minWidth: 90,
                                                        borderRadius: 4
                                                    }}
                                                />
                                            </div>
                                        )
                                    };

                                case 'coins_amount':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '60px' }}
                                            />
                                        )
                                    };

                                case 'description':
                                case 'cancellation_reason':
                                case 'disabling_reason':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '150px' }}
                                            />
                                        )
                                    };

                                case 'created_at':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '140px' }}
                                            />
                                        )
                                    };

                                case 'actions':
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Avatar
                                                active
                                                size="small"
                                                shape="square"
                                                style={{
                                                    width: 32,
                                                    height: 32
                                                }}
                                            />
                                        )
                                    };

                                default:
                                    return {
                                        ...column,
                                        render: () => (
                                            <Skeleton.Input
                                                active
                                                size="small"
                                                style={{ width: '100px' }}
                                            />
                                        )
                                    };
                            }
                        })}
                        dataSource={Array(pageSize).fill({})}
                        pagination={false}
                        rowKey={(_, index) => `skeleton-row-${index}`}
                        scroll={{ x: 800 }}
                        className="skeleton-table"
                    />
                ) : (
                    <AppTable
                        columns={columns}
                        dataSource={data}
                        loading={false}
                        rowKey="id"
                        scroll={{ x: 800 }}
                        className={TABLE_CLASS}
                        pagination={{
                            ...pagination,
                            current: page,
                            pageSize: pageSize,
                        }}
                        onChange={handleTableChange}
                        rowClassName={(record) => getRowClassName(record, getHighlightClass)}
                    />
                )}
            </Card>
        </Space>
    );
};

export default TransactionTable;
